/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    commerceApiRequest,
    commerceVideoMetadataRequest,
    getCatalogId,
    IActionContext,
    IImageData,
    IImageSettings,
    IVideoData
} from '@msdyn365-commerce/core';
import { MediaLocation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, MediaLocationsForSelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import { getAltText } from './helper';
import { isValidImage } from '../../../shared/functions/is-valid-image';
import { getMediaLocationsForSelectedVariantAction } from '../../../actions/get-media-locations-for-selected-variant.action';

export const MEDIA_LIBRARY_URL_INDICATOR = 'images-eu-prod';

async function validateMediaLocationAsync(mediaLocation: MediaLocation, filterLegacy?: boolean): Promise<[IImageData, boolean]> {
    const imageData: IImageData = {
        src: mediaLocation.Uri || '',
        altText: mediaLocation.AltText || ''
    };
    //BS CUSTOM
    if (mediaLocation.Uri && filterLegacy) {
        if (!isValidImage(mediaLocation.Uri)) {
            return [imageData, false];
        }
    }
    if (mediaLocation.IsApplicableForChildEntities) {
        imageData.additionalProperties = { isApplicableForChildEntities: 'true' };
    }

    if (imageData.src === '') {
        return [imageData, false];
    }
    return new Promise<[IImageData, boolean]>(resolve => {
        try {
            const http = new XMLHttpRequest();
            http.open('HEAD', imageData.src!, true);

            http.addEventListener('load', () => {
                resolve([imageData, http.status === 200 || http.status === 201]);
            });

            http.addEventListener('error', () => {
                resolve([imageData, false]);
            });

            http.send();
        } catch {
            resolve([imageData, false]);
        }
    });
}

export async function getValidProductImages(
    productId: number,
    channelId: number,
    actionContext: IActionContext,
    imageSettings: IImageSettings,
    selectedProduct?: SimpleProduct,
    filterLegacy?: boolean
): Promise<IImageData[]> {
    const catalogId = getCatalogId(actionContext.requestContext);
    const actionInput = new MediaLocationsForSelectedVariantInput(productId, channelId, selectedProduct, catalogId);

    return getMediaLocationsForSelectedVariantAction(actionInput, actionContext)
        .then((mediaLocations: MediaLocation[]) => {
            if (mediaLocations) {
                return Promise.all(
                    mediaLocations.map((mediaLocation: MediaLocation) => validateMediaLocationAsync(mediaLocation, filterLegacy))
                ).then(pairs => {
                    const validImages = pairs.filter(pair => pair[1]).map(pair => pair[0]);
                    return selectValidImages(validImages);
                });
            }

            return [];
        })
        .catch((error: any) => {
            actionContext.telemetry.exception(error);
            actionContext.telemetry.debug('Unable to get Media Locations for Selected Variant');
            return [];
        });
}

function selectValidImages(validImages: IImageData[]): IImageData[] {
    // Condition 1: Prefer media library images
    const mediaLibraryImages = validImages.filter(img => img.src && img.src.includes(MEDIA_LIBRARY_URL_INDICATOR));
    if (mediaLibraryImages.length > 0) {
        return mediaLibraryImages;
    }

    // Condition 2: If all are "variations", return all
    const allAreVariations = validImages.every(img => img.src && img.src.includes('variations'));
    if (allAreVariations) {
        return validImages;
    }

    // Condition 3: Otherwise, exclude "variations"
    return validImages.filter(img => img.src && !img.src.includes('variations'));
}

/**
 * Gets product variant images on dimension change of buy-box module for Omni scenario.
 * @param productId Product record id.
 * @param shouldUseCmsAltText Alt-text source to use.
 * @param channelId Current channel id.
 * @param actionContext Current action context.
 * @param productName Product name.
 * @param selectedProduct Currently selected variant product.
 * @returns List of images.
 */

export async function getProductImages(
    productId: number,
    shouldUseCmsAltText: boolean,
    channelId: number,
    actionContext: IActionContext,
    productName?: string,
    selectedProduct?: SimpleProduct
): Promise<IImageData[]> {
    const catalogId = getCatalogId(actionContext.requestContext);
    if (actionContext.requestContext?.params?.isPreview) {
        const response = await commerceApiRequest(actionContext.requestContext, 'msdyn365-ochannel-product-info', 'post', {
            productId: productId,
            channelId: channelId,
            catalogId: catalogId
        });
        if (response.status !== 200) {
            actionContext.telemetry.debug('Unable to get Media Locations for Selected Variant for preview scenario');
            return [];
        }
        const previewImageData = response?.data as MediaLocation[];
        if (previewImageData) {
            return previewImageData.map(mediaLocation => {
                return {
                    src: mediaLocation.Uri ?? '',
                    altText: getAltText(actionContext.requestContext, shouldUseCmsAltText, productName, mediaLocation.AltText),
                    displayOrder: mediaLocation.DisplayOrder,
                    mediaTypeValue: mediaLocation.MediaTypeValue,
                    additionalProperties: mediaLocation.IsApplicableForChildEntities
                        ? {
                              isApplicableForChildEntities: 'true'
                          }
                        : undefined
                };
            });
        }
        return [];
    }
    const actionInput = new MediaLocationsForSelectedVariantInput(productId, channelId, selectedProduct, catalogId);
    return getMediaLocationsForSelectedVariantAction(actionInput, actionContext)
        .then(mediaLocations => {
            if (mediaLocations) {
                return mediaLocations.map(mediaLocation => {
                    return {
                        src: mediaLocation.Uri ?? '',
                        altText: getAltText(actionContext.requestContext, shouldUseCmsAltText, productName, mediaLocation.AltText),
                        displayOrder: mediaLocation.DisplayOrder,
                        mediaTypeValue: mediaLocation.MediaTypeValue,
                        additionalProperties: mediaLocation.IsApplicableForChildEntities
                            ? {
                                  isApplicableForChildEntities: 'true'
                              }
                            : undefined
                    };
                });
            }
            return [];
        })
        .catch(error => {
            actionContext.telemetry.exception(error);
            actionContext.telemetry.debug('Unable to get Media Locations for Selected Variant');
            return [];
        });
}

export async function validateProductImages(
    mediaLocations: MediaLocation[],
    actionContext: IActionContext,
    imageSettings: IImageSettings,
    filterLegacy?: boolean
): Promise<IImageData[]> {
    return Promise.all(mediaLocations.map(mediaLocation => validateMediaLocationAsync(mediaLocation, filterLegacy)))
        .then(pairs => {
            return pairs.filter(pair => pair[1]).map(pair => pair[0]);
        })
        .catch(error => {
            actionContext.telemetry.exception(error);
            actionContext.telemetry.debug('Unable to validate product images');
            return [];
        });
}

/**
 * GetVideoMetadataFromAPI - Makes API call to get video metadata.
 * @param videoId: Video Id
 * @param actionContext: Action context
 * @returns: Returns the meta data of the video.
 */
export async function getVideoMetadataFromApi(videoId: string, actionContext: IActionContext): Promise<IVideoData | undefined> {
    let videoPlayerData: IVideoData | undefined;
    if (videoId) {
        const response = await commerceVideoMetadataRequest(actionContext.requestContext, [videoId]);

        if (response.status !== 200) {
            actionContext.telemetry.debug(`Unable to get video metadata for videoId: ${videoId}`);
            return;
        }

        const videoData = response.data as [];
        if (videoData && ArrayExtensions.hasElements(videoData)) {
            videoPlayerData = videoData[0];
        }
    }
    return videoPlayerData;
}
